/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from '../store'
import { useCookies } from 'react-cookie'

import { SimulationChart } from './SimulationChart'
import { SimulationResult } from './SimulationResult'
import { Loading } from './Loading'
import { SimulationGuideModal } from './SimulationGuideModal'
import { SimulationErrorModal } from './SimulationErrorModal'
import { SimulationForm } from './SimulationForm'
import '../assets/css/libs.css'
import '../assets/css/style.css'
import { Header } from '../components/header'
import { Footer } from '../components/footer'
import { Link } from 'react-router-dom'
import { useHeadTitle } from '../hook/useHeadTitle'
import { useDealerCodeUrl } from '../hook/useDealerCode'
import { SimulationTermsModal } from './SimulationTermsModal'
import { useDescription } from '../hook/useDescription'
import { reset } from '../store/modules/simulation'
import { DisclaimerModal } from './DisclaimerModal'

export interface FormInput {
  initial: number
  addition: number
  ret: number
  risk: number
  additionRet?: number
  additionRisk?: number
  depositRet: number
  years: number
}

export const defaultValues = {
  initial: 100,
  addition: 1.0,
  ret: 1.0,
  risk: 3.0,
  additionRet: 1.0,
  additionRisk: 3.0,
  years: 20,
  depositRet: 0.1,
}

export const constraintValues = {
  initial: {
    upper: 99999.9,
    lower: 0,
    decimalPoint: 1,
  },
  addition: {
    upper: 999.9,
    lower: 0,
    decimalPoint: 1,
  },
  ret: {
    upper: 99.9,
    lower: 0,
    decimalPoint: 1,
  },
  risk: {
    upper: 99.9,
    lower: 0,
    decimalPoint: 1,
  },
  additionRet: {
    upper: 99.9,
    lower: 0,
    decimalPoint: 1,
  },
  additionRisk: {
    upper: 99.9,
    lower: 0,
    decimalPoint: 1,
  },
  years: {
    upper: 40,
    lower: 1,
    decimalPoint: 0,
  },
  depositRet: {
    upper: 99.99,
    lower: 0,
    decimalPoint: 2,
  },
}

export const termsCookieValue = {
  agree: '1',
  disagree: '0',
}

export function Simulation() {
  const dispatch = useDispatch()
  const { result, fetchState } = useSelector((state: AppState) => state.simulation)
  const [inputErrorMessage, setInputErrorMessage] = useState('')
  const [isShowGuide, setShowGuide] = useState(false)
  const [isShowTerms, setShowTerms] = useState(false)
  const [isShowDisclaimer, setShowDisclaimer] = useState(false)
  const [cookie, setCookie] = useCookies(['simulation-terms-agree'])

  const resetInputErrorMessage = useCallback(() => setInputErrorMessage(''), [setInputErrorMessage])

  useHeadTitle('今こそ資産運用を考えよう', '将来シミュレーション')
  useDescription(
    'シミュレーションを通じてご自身の資産運用を考えてみませんか。野村アセットマネジメントが資産運用に取り組む皆さまをサポートします。'
  )

  useEffect(() => {
    dispatch(reset())
    return () => {
      dispatch(reset())
    }
  }, [dispatch])

  useEffect(() => {
    if (cookie['simulation-terms-agree'] !== termsCookieValue.agree) {
      setShowTerms(true)
    }
  }, [cookie])

  const setAgreeCookie = useCallback(
    (value: string) => {
      setCookie('simulation-terms-agree', value)
    },
    [setCookie]
  )
  const isPending = fetchState === 'pending'

  return (
    <React.Fragment>
      <Header />
      <body className="include shorai">
        <main>
          <section>
            <div className="c-title-wrap">
              <h2 className="c-title--primary">将来シミュレーション</h2>
              <p className="c-title__lead">あなたの資産の将来を預金と比べてみましょう</p>
              <p className="u-text--right">
                <a href="#" className="c-title__link-info" onClick={() => setShowGuide(true)}>
                  使い方ガイド
                </a>
              </p>
            </div>
            <div className="u-bg--white">
              <div className="l-container">
                <div className="p-simulation">
                  <div className="p-simulation__container">
                    <SimulationForm
                      setInputErrorMessage={setInputErrorMessage}
                      isPending={isPending}
                      isResponsive={true}
                    />
                    <SimulationResult />
                    <Loading apiStatus={fetchState} />
                    <SimulationChart result={result} />
                    <div className="u-mt50px">
                      <p className="c-note u-mb20px" style={{ color: 'red' }}>
                        【お詫び】2026年6月29日（月）16：30 ～ 7月1日（水）22：10頃の間、当ページにおいて、一時的にシミュレーション結果のグラフの一部が表示されない事象が発生しておりました。現在は復旧しており、通常通りご利用いただける状態となっております。ご利用の皆様にはご迷惑をおかけしましたことを、深くお詫び申し上げます。原因の究明を進めるとともに、再発防止に努めてまいります。
                      </p>
                      <p className="c-note">
                        将来の運用成果を示唆あるいは保証するものではありません。
                        内容の正確性、完全性、適合性等を保証するものではありません。
                        当該情報の利用による投資等の損益はすべてお客さまに帰属し、野村アセットマネジメント株式会社は一切責任を負いません。
                        サービス内容、免責事項、詳細は
                        <a href="#" onClick={() => setShowDisclaimer(true)}>
                          こちら
                        </a>
                        を御覧ください。
                      </p>
                    </div>
                    <div className="c-button-group">
                      <Link
                        to={useDealerCodeUrl('/')}
                        className="c-button--primary c-button-arrow--back"
                      >
                        <span className="c-button__text--xs">TOPページへ戻る</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <DisclaimerModal isShow={isShowDisclaimer} setShow={setShowDisclaimer} />
            <SimulationGuideModal isShow={isShowGuide} setShow={setShowGuide} />
            <SimulationTermsModal
              isShow={isShowTerms}
              setShow={setShowTerms}
              setAgreeCookie={setAgreeCookie}
            />
            <SimulationErrorModal
              inputErrorMessage={inputErrorMessage}
              resetInputErrorMessage={resetInputErrorMessage}
            />
          </section>
          <div id="modal-root"></div>
        </main>
        <Footer />
      </body>
    </React.Fragment>
  )
}
